/**
 * Task-graph store + actions for the MU "Tasks" module.
 *
 * Holds the live canonical task graph (fetched from mu-api /api/tasks/*),
 * the current view mode (memory constellation vs task graph), and the
 * selected-milestone panel state (incl. its live NEXUS related-memories).
 *
 * v1 is read-only (poll-then-fetch). The signature poll is cheap; the full
 * graph is only refetched when something actually changed. SSE upgrade later.
 */

import {
  fetchTaskGraph, fetchTaskVersion, fetchMilestoneMemories, fetchTaskTree, fetchTaskEdge,
  type TaskGraphData, type TaskMilestone, type RelatedMemory, type TreeRec, type TaskEdge,
} from './api.svelte'

/** Which top-level view is showing. */
export const appView = $state<{ mode: 'constellation' | 'tasks' }>({ mode: 'constellation' })

export const taskGraph = $state<{
  data: TaskGraphData | null
  loading: boolean
  error: string | null
  sig: string | null
  lastFetchedAt: number | null
}>({
  data: null,
  loading: false,
  error: null,
  sig: null,
  lastFetchedAt: null,
})

export const milestonePanel = $state<{
  open: boolean
  milestone: TaskMilestone | null
  memories: RelatedMemory[]
  memLoading: boolean
  memError: string | null
}>({
  open: false,
  milestone: null,
  memories: [],
  memLoading: false,
  memError: null,
})

let _loadInFlight: Promise<void> | null = null

/** Fetch (or refetch) the full graph + current signature. */
export async function loadTaskGraph(): Promise<void> {
  if (_loadInFlight) return _loadInFlight
  taskGraph.loading = true
  taskGraph.error = null
  _loadInFlight = (async () => {
    try {
      const [data, sig] = await Promise.all([fetchTaskGraph(), fetchTaskVersion()])
      taskGraph.data = data
      taskGraph.sig = sig
      taskGraph.lastFetchedAt = Date.now()
    } catch (err) {
      taskGraph.error = (err as Error).message
    } finally {
      taskGraph.loading = false
      _loadInFlight = null
    }
  })()
  return _loadInFlight
}

/**
 * Poll the cheap signature; if it changed (or we have no data), refetch the
 * full graph. Returns true when the graph was (re)loaded. Safe to call on an
 * interval.
 */
export async function refreshIfChanged(): Promise<boolean> {
  const sig = await fetchTaskVersion()
  if (sig == null) return false
  if (taskGraph.data && sig === taskGraph.sig) return false
  await loadTaskGraph()
  return true
}

/** Select a milestone → open the panel + fetch its live related NEXUS memories. */
export async function selectMilestone(m: TaskMilestone): Promise<void> {
  closeConnectionPanel()  // the two detail panels are mutually exclusive
  milestonePanel.open = true
  milestonePanel.milestone = m
  milestonePanel.memories = []
  milestonePanel.memError = null
  if (!m.has_embedding) {
    // No embedding on the node, but the endpoint embeds title+desc fresh, so
    // it still works — only skip if there's genuinely nothing to embed.
  }
  milestonePanel.memLoading = true
  const reqId = m.id
  try {
    const mems = await fetchMilestoneMemories(m.id, 8)
    // Guard against out-of-order responses if the user clicked another node.
    if (milestonePanel.milestone?.id === reqId) milestonePanel.memories = mems
  } catch (err) {
    if (milestonePanel.milestone?.id === reqId) milestonePanel.memError = (err as Error).message
  } finally {
    if (milestonePanel.milestone?.id === reqId) milestonePanel.memLoading = false
  }
}

export function closeMilestonePanel(): void {
  milestonePanel.open = false
  milestonePanel.milestone = null
  milestonePanel.memories = []
}

/* Connection (flow-edge) detail — the skill/procedure/audit metadata a Sankey
 * link-click "dives into". Mutually exclusive with the milestone panel. */
export const connectionPanel = $state<{
  open: boolean
  edge: TaskEdge | null
  loading: boolean
  error: string | null
}>({ open: false, edge: null, loading: false, error: null })

let _connSeq = 0
export async function selectConnection(fromTid: string | null, toTid: string): Promise<void> {
  closeMilestonePanel()
  connectionPanel.open = true
  connectionPanel.edge = null
  connectionPanel.error = null
  connectionPanel.loading = true
  const seq = ++_connSeq
  try {
    const edge = await fetchTaskEdge(toTid, fromTid)
    if (seq === _connSeq) connectionPanel.edge = edge
  } catch (err) {
    if (seq === _connSeq) connectionPanel.error = (err as Error).message
  } finally {
    if (seq === _connSeq) connectionPanel.loading = false
  }
}
export function closeConnectionPanel(): void {
  connectionPanel.open = false
  connectionPanel.edge = null
}

/* ────────────────────────────────────────────────────────────────────────
 * Shared fractal hierarchy — the one expandable tree behind every lens.
 *
 * `/api/tasks/tree` returns the whole process-commons hierarchy as flat
 * records with a parent pointer:
 *   all → ns:<namespace> → g:<graph> → w:<way/track> → n:<node/milestone>
 *   …and a node that `decomposes_to` a sub-way nests w:<sub> UNDER n:<node>,
 *   recursing to full DAG→HTN→DAG depth.
 *
 * DAG / Gantt / Sankey all render a slice of THIS tree against ONE shared
 * `expansion.open` set, so "click a node to reveal its depth" behaves
 * identically in every lens (expand-in-place — children nest inline). The
 * tree is loaded once; the depth is walked client-side.
 * ──────────────────────────────────────────────────────────────────────── */
export const treeState = $state<{
  recs: TreeRec[]
  byId: Map<string, TreeRec>
  childrenOf: Map<string, TreeRec[]>
  loading: boolean
  error: string | null
  loaded: boolean
}>({ recs: [], byId: new Map(), childrenOf: new Map(), loading: false, error: null, loaded: false })

/** Tree ids currently expanded-in-place (open, showing children inline). */
export const expansion = $state<{ open: Set<string> }>({ open: new Set<string>() })

export function isExpandable(id: string): boolean {
  return (treeState.childrenOf.get(id)?.length ?? 0) > 0
}
/** Toggle a node open/closed. Reassigns the Set so $derived consumers refire. */
export function toggleExpand(id: string): void {
  if (!isExpandable(id)) return
  const next = new Set(expansion.open)
  if (next.has(id)) next.delete(id); else next.add(id)
  expansion.open = next
}
/** Expand a node (idempotent) — used by "follow this flow" (link clicks). */
export function expandNode(id: string): void {
  if (!isExpandable(id) || expansion.open.has(id)) return
  const next = new Set(expansion.open); next.add(id); expansion.open = next
}
export function collapseAll(): void { expansion.open = new Set<string>() }

let _treeInFlight: Promise<void> | null = null
export async function loadTree(): Promise<void> {
  if (treeState.loaded) return
  if (_treeInFlight) return _treeInFlight
  treeState.loading = true; treeState.error = null
  _treeInFlight = (async () => {
    try {
      const recs = await fetchTaskTree()
      const byId = new Map(recs.map((r) => [r.id, r]))
      const childrenOf = new Map<string, TreeRec[]>()
      for (const r of recs) {
        if (r.parent == null) continue
        const arr = childrenOf.get(r.parent)
        if (arr) arr.push(r); else childrenOf.set(r.parent, [r])
      }
      treeState.recs = recs; treeState.byId = byId; treeState.childrenOf = childrenOf; treeState.loaded = true
    } catch (e) {
      treeState.error = (e as Error).message
    } finally {
      treeState.loading = false; _treeInFlight = null
    }
  })()
  return _treeInFlight
}

export type VisRec = { rec: TreeRec; depth: number; hasKids: boolean; open: boolean }
/** Pre-order slice rooted at `rootId`: its children, plus the descendants of
 *  every OPEN node, each annotated with depth/hasKids/open. Each lens lays this
 *  out in its own idiom. Reads expansion.open + treeState so it's reactive. */
export function visibleSubtree(rootId: string): VisRec[] {
  const out: VisRec[] = []
  const open = expansion.open
  const co = treeState.childrenOf
  const walk = (id: string, depth: number, guard: number) => {
    if (guard > 64) return // pathological-cycle backstop; the tree is acyclic
    for (const k of co.get(id) ?? []) {
      const hasKids = (co.get(k.id)?.length ?? 0) > 0
      const isOpen = open.has(k.id)
      out.push({ rec: k, depth, hasKids, open: isOpen })
      if (isOpen && hasKids) walk(k.id, depth + 1, guard + 1)
    }
  }
  walk(rootId, 0, 0)
  return out
}
